import { friendlyAutoscoredComment } from 'helpers/domain-specific/autoscored-mark.helpers';
import type { UiCategory, UiQuestion } from 'hooks/marking/ui-categories-builder';
import type { BaseTag } from 'services/assist-api/entities/tag/tags';
import type {
  MarkItem,
  TestInstanceResponseMarkWithItems
} from 'services/assist-api/entities/test-instance-response-mark/types';
import {
  MarkStatus,
  MarkType
} from 'services/assist-api/entities/test-instance-response-mark/types';
import { TestInstanceResponseInputType } from 'services/assist-api/entities/test-instance-response/types';
import type { TestRubricItemWithOptions } from 'services/assist-api/entities/test-rubric-item/types';
import {
  type AttentionReasonFormValue,
  type MarkResponseFormValues,
  SubmitAction
} from './mark-response-form.schema';

function markItemFromQuestionAndRubricItem(
  markItems: MarkItem[] | undefined,
  question: UiQuestion,
  rubricItem: TestRubricItemWithOptions
): MarkItem | undefined {
  return markItems?.find(
    (markItem) =>
      markItem.testRubricItem.id === rubricItem.id &&
      // Update this once/if we need to support multiple items per question:
      markItem.testInstanceResponseItem.id === question.responseItems[0]?.id
  );
}

export function loadDefaultValues(
  categories: UiCategory[],
  attentionReasons: BaseTag[],
  currentUserMarkerId: number | undefined,
  activeMarkerId: number | undefined,
  mark?: TestInstanceResponseMarkWithItems,
  oldMark?: TestInstanceResponseMarkWithItems
): MarkResponseFormValues {
  return {
    // A mark item form value is created for every rubric item, even if that
    // mark item isn't loaded or saved. This is so that React Hook Form can
    // display error messages for each mark item individually.
    markItems: Array.from(
      categories.flatMap((category) =>
        category.questions.flatMap((question) =>
          question.rubricItems.flatMap((rubricItem) => ({ question, rubricItem }))
        )
      ),
      ({ question, rubricItem }) => {
        const markItem = markItemFromQuestionAndRubricItem(mark?.markItems, question, rubricItem);
        const oldMarkItem = markItemFromQuestionAndRubricItem(
          oldMark?.markItems,
          question,
          rubricItem
        );
        // If there is an old mark item (from an autoscored mark being replaced), make a copy of the
        // submittedValue, testRubricItem.id (from which the submittedScore is determined by the
        // API), and inputType of that mark item in the new mark item so the manual marker can
        // continue where the AI left off:
        return {
          id: markItem?.id,
          submittedValue: oldMarkItem?.submittedValue || markItem?.submittedValue || '', // Ensure null or undefined submitted values are loaded as a string
          rubricItemId: rubricItem.id,
          rubricItemOptionId:
            oldMarkItem?.submittedOptions?.at(0)?.id || markItem?.submittedOptions?.at(0)?.id,
          // For CIPS Writing, there should only ever be one response item per
          // question. If we later support marking for multiple response items
          // per question, this will need to be updated:
          responseItemId: question.responseItems[0]?.id,
          marker: markItem?.testInstanceResponseMarker
            ? {
                id: markItem?.testInstanceResponseMarker?.id,
                organisationUserId: markItem?.testInstanceResponseMarker?.user.id
              }
            : undefined,
          inputType:
            oldMarkItem?.inputType || markItem?.inputType || TestInstanceResponseInputType.Manual
        };
      }
    ),
    status: mark?.status || MarkStatus.Assigned,
    flagForAttention: mark?.needsAttention || false,
    hasAskedToFlagForAttention: false,
    submitForReview: false,
    attentionReasons: attentionReasons.map(
      (attentionReason: BaseTag): AttentionReasonFormValue => ({
        id: attentionReason.id,
        identifier: attentionReason.identifier,
        checked: !!mark?.tags?.find((t) => t.id === attentionReason.id)
      })
    ),
    comment:
      mark?.type === MarkType.Autoscored && mark?.comment
        ? friendlyAutoscoredComment(mark?.comment)
        : mark?.comment ?? '', // Ensure null or undefined comments are loaded as a string
    currentUserMarkerId,
    activeMarkerId,
    otherMarkingKnown: false,
    submitAction: SubmitAction.SaveDraftMarking
  };
}
